import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Modal,
  Button,
  Form,
  Container,
  Row,
  Col,
  Card,
  Table,
  Alert,
  Tabs,
  Tab,
  ListGroup,
} from 'react-bootstrap';
import GridPagination from '../components/GridPagination';
import OperationProgress from '../components/OperationProgress';
import { useCopyProgress } from '../hooks/useCopyProgress';
import EditListField from '../components/EditListField';
import MissingSelectOption from '../components/MissingSelectOption';
import { SALES_ITEMS_INDIVIDUAL_PROP_FIELDS } from '../constants/salesItemsIndividual';
import {
  validateSalesItemIndividualForm,
  validateField,
  ValidationRules,
  hasErrors,
  formatErrors,
} from '../utils/validation';

function emptyForm() {
  const form = {
    main_stock_no: '',
    serial_no: '',
    name: '',
    purch_price: '',
    purch_date: '',
    sale_price: '',
    depot: '',
    make: '',
    model: '',
  };
  SALES_ITEMS_INDIVIDUAL_PROP_FIELDS.forEach((field) => {
    form[field] = '';
  });
  return form;
}

function toForm(record) {
  if (!record) {
    return emptyForm();
  }
  const form = emptyForm();
  Object.keys(form).forEach((key) => {
    if (record[key] != null && record[key] !== '') {
      if (key === 'purch_date') {
        form[key] = record[key] ? String(record[key]).split('T')[0] : '';
      } else {
        form[key] = record[key];
      }
    }
  });
  return form;
}

const SYSTEM_FIELDS = new Set(['id', 'created_at', 'updated_at', 'tenant_id', 'user_id']);

const GRID_FIELD_LABELS = {
  name: 'Name',
  serial_no: 'Serial number',
  make: 'Make',
  model: 'Model',
  purch_date: 'Purchase date',
  sale_price: 'Sale price',
};

function buildRecordPayload(record, overrides = {}) {
  const payload = {};
  for (const [key, value] of Object.entries(record)) {
    if (!SYSTEM_FIELDS.has(key)) {
      payload[key] = value;
    }
  }
  return { ...payload, ...overrides };
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function nextNumberedValue(originalValue, allValues, index, defaultPadLength = 2) {
  if (!originalValue) {
    return originalValue;
  }

  const match = String(originalValue).match(/^(.*?)(\d+)$/);
  const baseValue = match ? match[1] : String(originalValue);
  const padLength = match ? match[2].length : defaultPadLength;
  const pattern = new RegExp(`^${escapeRegExp(baseValue)}(\\d+)$`);

  const existingNumbers = allValues
    .map((value) => {
      const itemMatch = String(value || '').match(pattern);
      return itemMatch ? parseInt(itemMatch[1], 10) : 0;
    })
    .filter((num) => num > 0);

  const highestNum = existingNumbers.length > 0 ? Math.max(...existingNumbers) : 0;
  const newNumber = highestNum + 1 + index;
  return `${baseValue}${String(newNumber).padStart(padLength, '0')}`;
}

function buildCopyPayload(record, allRecords, index = 0) {
  const payload = buildRecordPayload(record);
  payload.main_stock_no = nextNumberedValue(
    record.main_stock_no,
    allRecords.map((item) => item.main_stock_no),
    index
  );
  if (record.serial_no) {
    payload.serial_no = nextNumberedValue(
      record.serial_no,
      allRecords.filter((item) => item.serial_no).map((item) => item.serial_no),
      index
    );
  }
  return payload;
}

function formatGridEditValue(row, field) {
  if (field === 'purch_date') {
    return row.purch_date ? String(row.purch_date).split('T')[0] : '';
  }
  if (field === 'sale_price') {
    return row.sale_price == null || row.sale_price === '' ? '' : String(row.sale_price);
  }
  return row[field] == null ? '' : String(row[field]);
}

function formatGridDisplayValue(row, field, formatDate) {
  if (field === 'purch_date') {
    return formatDate(row.purch_date);
  }
  if (field === 'sale_price') {
    return row.sale_price ?? '—';
  }
  return row[field] || '—';
}

function parseGridSaveValue(field, raw) {
  if (field === 'purch_date') {
    return raw || null;
  }
  if (field === 'sale_price') {
    return raw === '' ? null : raw;
  }
  const trimmed = raw.trim();
  if (field === 'name') {
    return trimmed;
  }
  return trimmed || null;
}

function gridValuesEqual(field, current, next) {
  if (field === 'sale_price') {
    const currentNum = current == null || current === '' ? null : Number(current);
    const nextNum = next == null || next === '' ? null : Number(next);
    if (currentNum === null && nextNum === null) {
      return true;
    }
    if (currentNum === null || nextNum === null || Number.isNaN(nextNum)) {
      return String(current ?? '') === String(next ?? '');
    }
    return currentNum === nextNum;
  }
  if (field === 'purch_date') {
    const currentDate = current ? String(current).split('T')[0] : '';
    const nextDate = next ? String(next).split('T')[0] : '';
    return currentDate === nextDate;
  }
  const currentText = current == null ? '' : String(current).trim();
  const nextText = next == null ? '' : String(next).trim();
  return currentText === nextText;
}

function EditableGridCell({
  row,
  field,
  type = 'text',
  editingCell,
  editingValue,
  savingCell,
  onStartEdit,
  onChange,
  onSave,
  onKeyDown,
  maxLength,
  emphasize = false,
  formatDate,
}) {
  const cellKey = `${row.id}-${field}`;
  const isEditing = editingCell === cellKey;
  const isSaving = savingCell === cellKey;
  const displayValue = formatGridDisplayValue(row, field, formatDate);

  if (isEditing) {
    return (
      <Form.Control
        type={type}
        size="sm"
        value={editingValue}
        maxLength={maxLength}
        step={type === 'number' ? '0.01' : undefined}
        min={type === 'number' ? '0' : undefined}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => onSave(row, field)}
        onKeyDown={(e) => onKeyDown(e, row, field)}
        autoFocus
        disabled={isSaving}
        style={{ minWidth: type === 'date' ? '140px' : '100px' }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      role="button"
      tabIndex={0}
      className={emphasize ? 'fw-semibold' : undefined}
      style={{ cursor: 'pointer', display: 'inline-block', minWidth: '48px' }}
      onClick={() => onStartEdit(row, field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(row, field);
        }
      }}
      title="Click to edit"
    >
      {displayValue}
    </span>
  );
}

function formatSalesItemLabel(item) {
  if (!item) {
    return '';
  }
  return `${item.item_name} (${item.item_code})`;
}

function SalesItemIndividualModal({ record, onClose, onSave }) {
  const [formData, setFormData] = useState(emptyForm());
  const [salesItems, setSalesItems] = useState([]);
  const [depots, setDepots] = useState([]);
  const [stockSearch, setStockSearch] = useState('');
  const [showStockMatches, setShowStockMatches] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const [salesRes, depotsRes] = await Promise.all([
          axios.get('/api/sales-items/dropdown'),
          axios.get('/api/depots', { params: { page: 1, limit: 10000 } }),
        ]);
        setSalesItems(Array.isArray(salesRes.data) ? salesRes.data : []);
        setDepots(depotsRes.data.depots || []);
      } catch (error) {
        console.error('Failed to fetch lookup data:', error);
      }
    };
    fetchLookups();
  }, []);

  useEffect(() => {
    setFormData(toForm(record));
    setSaveError('');
    setValidationErrors({});
    setShowStockMatches(false);
  }, [record]);

  useEffect(() => {
    const code = formData.main_stock_no;
    if (!code) {
      setStockSearch('');
      return;
    }
    const match = salesItems.find((item) => item.item_code === code);
    setStockSearch(match ? formatSalesItemLabel(match) : code);
  }, [formData.main_stock_no, salesItems, record]);

  const filteredSalesItems = useMemo(() => {
    const query = stockSearch.trim().toLowerCase();
    const items = query
      ? salesItems.filter((item) => {
          const code = (item.item_code || '').toLowerCase();
          const name = (item.item_name || '').toLowerCase();
          return code.includes(query) || name.includes(query);
        })
      : salesItems;

    return items.slice(0, 20);
  }, [salesItems, stockSearch]);

  const handleSelectSalesItem = (item) => {
    setFormData((prev) => ({
      ...prev,
      main_stock_no: item.item_code,
      name: item.item_name || '',
    }));
    setStockSearch(formatSalesItemLabel(item));
    setShowStockMatches(false);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next.main_stock_no;
      delete next.name;
      return next;
    });
    setSaveError('');
  };

  const handleStockSearchChange = (value) => {
    setStockSearch(value);
    setShowStockMatches(true);

    const trimmed = value.trim().toLowerCase();
    const exactMatch = salesItems.find(
      (item) =>
        item.item_code?.toLowerCase() === trimmed ||
        formatSalesItemLabel(item).toLowerCase() === trimmed
    );

    setFormData((prev) => {
      if (exactMatch) {
        return {
          ...prev,
          main_stock_no: exactMatch.item_code,
          name: exactMatch.item_name || '',
        };
      }
      return { ...prev, main_stock_no: '' };
    });

    if (validationErrors.main_stock_no) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next.main_stock_no;
        return next;
      });
    }
    setSaveError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
    setSaveError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateSalesItemIndividualForm(formData);
    if (hasErrors(errors)) {
      setValidationErrors(errors);
      setSaveError(formatErrors(errors));
      return;
    }
    setValidationErrors({});
    setSaveError('');
    try {
      await onSave(formData);
    } catch (err) {
      const message = err.response?.data?.error || 'Failed to save sales item individual';
      setSaveError(message);
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="xl" style={{ zIndex: 1060 }}>
      <Modal.Header closeButton>
        <Modal.Title>
          {record ? 'Edit Sales Item Individual' : 'Add Sales Item Individual'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {saveError && (
          <Alert variant="danger" className="mb-3">
            {saveError}
          </Alert>
        )}
        <Form noValidate onSubmit={handleSubmit}>
          <Tabs defaultActiveKey="details" className="mb-3">
            <Tab eventKey="details" title="Details">
              <Container fluid>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3 position-relative">
                      <Form.Label>Main Stock No *</Form.Label>
                      <Form.Control
                        type="search"
                        name="main_stock_no_search"
                        placeholder="Search by sales item name or code..."
                        value={stockSearch}
                        onChange={(e) => handleStockSearchChange(e.target.value)}
                        onFocus={() => setShowStockMatches(true)}
                        onBlur={() => {
                          window.setTimeout(() => setShowStockMatches(false), 150);
                        }}
                        autoComplete="off"
                        isInvalid={!!validationErrors.main_stock_no}
                      />
                      {formData.main_stock_no && (
                        <Form.Text className="text-muted">
                          Selected code: {formData.main_stock_no}
                        </Form.Text>
                      )}
                      {showStockMatches && filteredSalesItems.length > 0 && (
                        <ListGroup
                          className="position-absolute w-100 shadow-sm"
                          style={{ zIndex: 2, maxHeight: '220px', overflowY: 'auto' }}
                        >
                          {filteredSalesItems.map((item) => (
                            <ListGroup.Item
                              key={item.id}
                              action
                              onMouseDown={(event) => event.preventDefault()}
                              onClick={() => handleSelectSalesItem(item)}
                            >
                              <div className="fw-medium">{item.item_name}</div>
                              <div className="small text-muted">Code: {item.item_code}</div>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      )}
                      {showStockMatches &&
                        stockSearch.trim() &&
                        filteredSalesItems.length === 0 && (
                          <div className="small text-muted mt-1">No matching sales items found</div>
                        )}
                      {validationErrors.main_stock_no && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {validationErrors.main_stock_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        maxLength={120}
                        isInvalid={!!validationErrors.name}
                      />
                      {validationErrors.name && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.name}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Serial No</Form.Label>
                      <Form.Control
                        type="text"
                        name="serial_no"
                        value={formData.serial_no}
                        onChange={handleChange}
                        maxLength={50}
                        isInvalid={!!validationErrors.serial_no}
                      />
                      {validationErrors.serial_no && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.serial_no}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Depot</Form.Label>
                      <Form.Select
                        name="depot"
                        value={formData.depot}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.depot}
                      >
                        <option value="">Select a depot...</option>
                        <MissingSelectOption
                          value={formData.depot}
                          options={depots}
                          getOptionValue={(depot) => depot.short_code}
                        />
                        {depots.map((depot) => (
                          <option key={depot.id} value={depot.short_code}>
                            {depot.depot_name}
                          </option>
                        ))}
                      </Form.Select>
                      {validationErrors.depot && (
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {validationErrors.depot}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Price</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        name="purch_price"
                        value={formData.purch_price}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.purch_price}
                      />
                      {validationErrors.purch_price && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.purch_price}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Purchase Date</Form.Label>
                      <Form.Control
                        type="date"
                        name="purch_date"
                        value={formData.purch_date}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.purch_date}
                      />
                      {validationErrors.purch_date && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.purch_date}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Sale Price</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        name="sale_price"
                        value={formData.sale_price}
                        onChange={handleChange}
                        isInvalid={!!validationErrors.sale_price}
                      />
                      {validationErrors.sale_price && (
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.sale_price}
                        </Form.Control.Feedback>
                      )}
                    </Form.Group>

                    <EditListField
                      inputName="make"
                      label="Make"
                      active
                      refreshKey={record?.id ?? 'new'}
                      valuesUrl="/api/sales-items-individual/distinct-values?field=make"
                      value={formData.make}
                      onChange={(make) => handleFieldChange('make', make)}
                      maxLength={30}
                    />

                    <EditListField
                      inputName="model"
                      label="Model"
                      active
                      refreshKey={record?.id ?? 'new'}
                      valuesUrl="/api/sales-items-individual/distinct-values?field=model"
                      value={formData.model}
                      onChange={(model) => handleFieldChange('model', model)}
                      maxLength={128}
                    />
                  </Col>
                </Row>
              </Container>
            </Tab>

            <Tab eventKey="properties" title="Properties">
              <Container fluid>
                {Array.from({ length: 10 }, (_, rowIndex) => (
                  <Row key={rowIndex}>
                    {SALES_ITEMS_INDIVIDUAL_PROP_FIELDS.slice(rowIndex * 5, rowIndex * 5 + 5).map(
                      (field, index) => {
                        const propNumber = rowIndex * 5 + index + 1;
                        return (
                          <Col md={2} key={field}>
                            <Form.Group className="mb-3">
                              <Form.Label>{`Prop${propNumber}`}</Form.Label>
                              <Form.Control
                                type="text"
                                name={field}
                                value={formData[field] || ''}
                                onChange={handleChange}
                                maxLength={120}
                                isInvalid={!!validationErrors[field]}
                              />
                              {validationErrors[field] && (
                                <Form.Control.Feedback type="invalid">
                                  {validationErrors[field]}
                                </Form.Control.Feedback>
                              )}
                            </Form.Group>
                          </Col>
                        );
                      }
                    )}
                  </Row>
                ))}
              </Container>
            </Tab>
          </Tabs>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit}>
          {record ? 'Update' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

function SalesItemsIndividual() {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [copyingId, setCopyingId] = useState(null);
  const { copyProgress, wrapCopy } = useCopyProgress();
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyItem, setCopyItem] = useState(null);
  const [copyCount, setCopyCount] = useState(1);
  const [editingCell, setEditingCell] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [savingCell, setSavingCell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
  });

  const fetchRecords = async (page = 1, search = '') => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get('/api/sales-items-individual', {
        params: { page, limit: pagination.limit, search },
      });
      setRecords(response.data.salesItemsIndividual || []);
      setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, pages: 0 });
    } catch (err) {
      setError(`Failed to fetch sales items individual: ${err.message}`);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(1, searchTerm);
  };

  const handlePageChange = (newPage) => {
    fetchRecords(newPage, searchTerm);
  };

  const handleSave = async (payload) => {
    try {
      if (editingRecord) {
        await axios.put(`/api/sales-items-individual/${editingRecord.id}`, payload);
        setSuccess('Sales item individual updated successfully');
      } else {
        await axios.post('/api/sales-items-individual', payload);
        setSuccess('Sales item individual created successfully');
      }
      setShowModal(false);
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }
    try {
      await axios.delete(`/api/sales-items-individual/${id}`);
      setSuccess('Record deleted successfully');
      fetchRecords(pagination.page, searchTerm);
    } catch {
      setError('Failed to delete record');
    }
  };

  const startGridEdit = (row, field) => {
    setEditingCell(`${row.id}-${field}`);
    setEditingValue(formatGridEditValue(row, field));
  };

  const cancelGridEdit = () => {
    setEditingCell(null);
    setEditingValue('');
  };

  const saveGridEdit = async (row, field) => {
    const cellKey = `${row.id}-${field}`;
    if (editingCell !== cellKey) {
      return;
    }

    const parsed = parseGridSaveValue(field, editingValue);
    if (gridValuesEqual(field, row[field], parsed)) {
      cancelGridEdit();
      return;
    }

    const fieldError = validateField(
      field,
      parsed,
      ValidationRules.salesItemsIndividual[field],
      row
    );
    if (fieldError) {
      setError(fieldError);
      cancelGridEdit();
      return;
    }

    setSavingCell(cellKey);
    try {
      const response = await axios.put(`/api/sales-items-individual/${row.id}`, {
        [field]: parsed,
      });
      setRecords((prev) =>
        prev.map((record) => (record.id === row.id ? { ...record, ...response.data } : record))
      );
      setSuccess(`${GRID_FIELD_LABELS[field] || 'Field'} updated`);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record');
    } finally {
      setSavingCell(null);
      cancelGridEdit();
    }
  };

  const handleGridKeyDown = (e, row, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveGridEdit(row, field);
    } else if (e.key === 'Escape') {
      cancelGridEdit();
    }
  };

  const handleCopyClick = (record) => {
    setCopyItem(record);
    setCopyCount(1);
    setShowCopyModal(true);
  };

  const handleCopySalesItemIndividual = async (record, count = 1) => {
    setCopyingId(record.id);
    setShowCopyModal(false);
    setError('');
    try {
      await wrapCopy(
        count === 1 ? 'Copying sales item...' : `Copying ${count} sales items...`,
        count,
        async (tick) => {
          for (let i = 0; i < count; i += 1) {
            await axios.post('/api/sales-items-individual', buildCopyPayload(record, records, i));
            tick({ success: true });
          }
        }
      );
      setSuccess(
        count === 1
          ? 'Sales item individual copied successfully'
          : `${count} sales items individual copied successfully`
      );
      fetchRecords(pagination.page, searchTerm);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to copy sales item individual');
    } finally {
      setCopyingId(null);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm('Are you sure you want to delete ALL sales items individual?')) {
      return;
    }
    if (
      !window.confirm(
        'This will permanently delete all sales items individual records. Are you absolutely sure?'
      )
    ) {
      return;
    }
    try {
      const response = await axios.delete('/api/sales-items-individual');
      setSuccess(`All records deleted successfully (${response.data.deletedCount || 0} records)`);
      fetchRecords(1, '');
    } catch {
      setError('Failed to delete all records');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Container fluid>
      <Row className="mt-0 mb-2">
        <Col>
          <div className="d-flex align-items-center">
            <i className="fas fa-cube me-3 text-primary" style={{ fontSize: '2.5rem' }}></i>
            <div>
              <h1 className="mb-0">Sales Items Individual</h1>
              <p className="text-muted mb-0">Manage individual sales stock items</p>
            </div>
          </div>
        </Col>
        <Col xs="auto" style={{ position: 'relative', zIndex: 1 }}>
          <Button
            variant="primary"
            onClick={() => {
              setEditingRecord(null);
              setShowModal(true);
            }}
            className="me-2"
          >
            Add Record
          </Button>
          <Button variant="danger" onClick={handleDeleteAll} disabled={records.length === 0}>
            Delete All
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {copyProgress && (
        <OperationProgress
          title={copyProgress.title}
          processed={copyProgress.processed}
          total={copyProgress.total}
          success={copyProgress.success}
          failed={copyProgress.failed}
          variant="info"
        />
      )}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row>
              <Col>
                <Form.Control
                  type="text"
                  placeholder="Search by stock no, name, serial no, make, or model..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Col>
              <Col xs="auto">
                <Button variant="primary" type="submit">
                  Search
                </Button>
              </Col>
            </Row>
          </Form>

          {loading ? (
            <div className="text-center p-3">Loading...</div>
          ) : records.length === 0 ? (
            <div className="text-center p-3">No records found.</div>
          ) : (
            <>
              <div className="table-responsive">
                <Table striped bordered hover className="mb-3">
                  <thead>
                    <tr>
                      <th>Main Stock No</th>
                      <th>Name</th>
                      <th>Serial No</th>
                      <th>Depot</th>
                      <th>Make</th>
                      <th>Model</th>
                      <th>Purchase Date</th>
                      <th>Sale Price</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <strong>{row.main_stock_no}</strong>
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="name"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={120}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="serial_no"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={50}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>{row.depot || '-'}</td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="make"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={30}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="model"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            maxLength={128}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="purch_date"
                            type="date"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <EditableGridCell
                            row={row}
                            field="sale_price"
                            type="number"
                            editingCell={editingCell}
                            editingValue={editingValue}
                            savingCell={savingCell}
                            onStartEdit={startGridEdit}
                            onChange={setEditingValue}
                            onSave={saveGridEdit}
                            onKeyDown={handleGridKeyDown}
                            formatDate={formatDate}
                          />
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => {
                                setEditingRecord(row);
                                setShowModal(true);
                              }}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleCopyClick(row)}
                              disabled={copyingId === row.id}
                            >
                              {copyingId === row.id ? 'Copying...' : 'Copy'}
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(row.id)}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <GridPagination
                page={pagination.page}
                pages={pagination.pages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </Card.Body>
      </Card>

      {showModal && (
        <SalesItemIndividualModal
          record={editingRecord}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}

      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Copy sales item individual</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {copyItem && (
            <p className="mb-3">
              How many copies of <strong>{copyItem.main_stock_no}</strong> ({copyItem.name}) would
              you like to create?
            </p>
          )}
          <Form.Group>
            <Form.Label>Number of copies</Form.Label>
            <Form.Control
              type="number"
              min={1}
              max={100}
              value={copyCount}
              onChange={(e) =>
                setCopyCount(Math.max(1, Math.min(100, parseInt(e.target.value, 10) || 1)))
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => copyItem && handleCopySalesItemIndividual(copyItem, copyCount)}
          >
            Create {copyCount} {copyCount === 1 ? 'copy' : 'copies'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default SalesItemsIndividual;
